import { type UserData, authService } from '@/services/AuthService';
import { type LoginCredentials, type RegistrationData } from '@/types/api';
import { Preferences } from '@capacitor/preferences';
import { defineStore } from 'pinia';

// import { accountProfileService } from '@/services/account/AccountProfileService';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null as UserData | null,
        authToken: null as string | null,
        isLoading: false,
        error: null as string | null,
        isAuthenticated: false,
        registrationEmail: null as string | null,
    }),
    getters: {
        getRegistrationEmail(state) {
            return state.registrationEmail;
        },
        isInVerificationProcess(state) {
            return state.isAuthenticated && state.registrationEmail !== null;
        },
        async getIsAuthenticated() {
            const token = await Preferences.get({ key: 'auth_token' });
            const expiry = await Preferences.get({ key: 'auth_expiry' });

            if (!token.value || !expiry.value) return false;

            return Date.now() < parseInt(expiry.value);
        },
        async getAuthToken() {
            const token = await Preferences.get({ key: 'auth_token' });
            return token.value;
        },
        async getUser() {
            const user = await Preferences.get({ key: 'auth_user' });
            return user.value ? JSON.parse(user.value) : null;
        },
    },
    actions: {
        setRegistrationEmail(email: string) {
            this.registrationEmail = email;
        },
        clearRegistrationEmail() {
            this.registrationEmail = null;
        },
        setRegistrationData(data: Record<string, any>) {
            this.registrationData = data;
        },
        clearRegistrationData() {
            this.registrationData = null;
        },
        async initialize() {
            // Skip if already authenticated with valid data AND the token
            // still exists in storage. Without the storage check, a stale
            // in-memory state (from the 401 interceptor clearing Preferences
            // but not the store) would cause the guard to skip re-validation
            // on every subsequent navigation, leaving the app in an
            // inconsistent "looks authenticated, all API calls 401" state.
            if (this.isAuthenticated && this.user && this.authToken) {
                const { value: storedToken } = await Preferences.get({ key: 'auth_token' });
                if (storedToken) {
                    return;
                }
            }

            this.error = null;

            try {
                const isValid = await authService.validateCurrentToken();
                if (isValid) {
                    const storedUser = await this.getUser;
                    const storedToken = await this.getAuthToken;

                    if (storedUser && storedToken) {
                        this.user = storedUser;
                        this.authToken = storedToken;
                        this.isAuthenticated = true;
                    }
                } else {
                    await authService.clearSession();
                    this.user = null;
                    this.authToken = null;
                    this.isAuthenticated = false;
                }
            } catch (err) {
                console.error('Auth initialization error:', err);
                this.error = 'Failed to initialize authentication.';
                await authService.clearSession();
                this.user = null;
                this.authToken = null;
                this.isAuthenticated = false;
            }
        },
        async login(credentials: LoginCredentials, redirect?: string) {
            try {
                const response = await authService.login(credentials);
                this.user = response.data;
                this.authToken = response.token;
                this.isAuthenticated = true;

                if (redirect) {
                    // Use router instance to redirect
                    this.router.push(redirect);
                }

                return response;
            } catch (err: any) {
                throw err;
            }
        },
        async register(userData: RegistrationData) {
            this.error = null;
            this.setRegistrationEmail(userData.email);

            try {
                const response = await authService.register(userData);
                this.user = response.data;
                this.authToken = response.token;
                this.isAuthenticated = true;
                return response;
            } catch (err: any) {
                this.error = err.message;
                throw err;
            }
        },
        async validateOTP(email: string, otp: string) {
            this.error = null;

            try {
                const response = await authService.validateOTP(email, otp);
                this.user = response.data;
                this.authToken = response.token;
                this.isAuthenticated = true;
                return response;
            } catch (error: any) {
                this.error = error.message;
                throw error;
            }
        },
        async resendOTP(email: string) {
            try {
                await authService.resendOTP(email);
            } catch (error) {
                throw error;
            }
        },

        async validatePassword(email: string, password: string, password_confirmation: string) {
            this.error = null;

            try {
                const response = await authService.validatePassword(
                    email,
                    password,
                    password_confirmation,
                );
                this.user = response.data;
                this.authToken = response.token;
                this.isAuthenticated = true;
                return response;
            } catch (error: any) {
                this.error = error.message;
                throw error;
            }
        },
        async resetPassword(email: string, password: string, passwordConfirmation: string) {
            this.error = null;

            try {
                await authService.resetPassword(email, {
                    password,
                    password_confirmation: passwordConfirmation,
                });
            } catch (error: any) {
                this.error = error.message;
                throw error;
            }
        },

        async logout() {
            try {
                await authService.logout();
                this.user = null;
                this.authToken = null;
                this.isAuthenticated = false;
            } catch (err) {
                throw err;
            }
        },
        async updateUser(userData: Partial<UserData>) {
            if (!this.user) {
                console.error('Cannot update user, user is not logged in.');
                return;
            }
            try {
                const updatedUserData: UserData = {
                    ...this.user,
                    ...userData,
                    id: this.user.id,
                };

                this.user = updatedUserData;
                await authService.updateStoredUser(updatedUserData);
            } catch (error) {
                console.error('Failed to update user:', error);
            }
        },
        async refreshUser() {
            if (!this.user) {
                console.error('Cannot refresh user, user is not logged in.');
                return null;
            }

            try {
                const refreshedUser = await authService.refreshAndUpdateUser();
                this.user = refreshedUser;
                return refreshedUser;
            } catch (error) {
                console.error('Failed to refresh user:', error);
                throw error;
            }
        },
        // async fetchAndUpdateUser() {
        //     if (!this.user) {
        //         console.error('Cannot update user, user is not logged in.');
        //         return;
        //     }
        //     try {
        //         const response = await accountProfileService.showInformation();
        //         const profileData = response.data;

        //         if (profileData) {
        //             const updatedUserData: UserData = {
        //                 ...this.user,
        //                 ...profileData,
        //                 id: this.user.id,
        //             };

        //             this.user = updatedUserData;
        //             await authService.updateStoredUser(updatedUserData);
        //         }
        //     } catch (error) {
        //         console.error('Failed to fetch and update user:', error);
        //     }
        // },
    },
});
